import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import MobileHeader from '../components/MobileHeader';
import { useRefreshOnVisible } from '../hooks/useRefreshOnVisible';
import ListManagement from './ListManagement';
import LocationAwareTasks from './LocationAwareTasks';
import RecurringTasks from './RecurringTasks';
import Preferences from './Preferences';
import Analytics from './Analytics';
import BrowserExtension from './BrowserExtension';
import './Account.css';

// Valid account sections
const VALID_SECTIONS = ['list-management', 'location-aware', 'recurring', 'preferences', 'analytics', 'browser-extension'];

export default function Account({ user, onUserUpdate }) {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(user);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const navigate = useNavigate();
  const { section } = useParams();

  // Derive active section from URL, default to list-management
  const activeSection = section || 'list-management';

  // Validate section and redirect if invalid
  useEffect(() => {
    if (section && !VALID_SECTIONS.includes(section)) {
      navigate('/account/list-management', { replace: true });
    }
  }, [section, navigate]);

  useEffect(() => {
    setCurrentUser(user);
  }, [user]);

  const handleUserUpdate = (updatedUser) => {
    setCurrentUser(updatedUser);
    if (onUserUpdate) {
      onUserUpdate(updatedUser);
    }
  };

  const loadLists = useCallback(async () => {
    try {
      // Account sections only need list metadata + counts, not the heavy per-task
      // payload -- use the lightweight endpoint so the page loads fast.
      const data = await api.getListsMetadata();
      setLists(data);
    } catch (error) {
      console.error('Error loading lists:', error);
      if (error.message.includes('401') || error.message.includes('authenticate')) {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Refresh data when app regains focus (tab/window becomes visible)
  useRefreshOnVisible(loadLists);

  useEffect(() => {
    loadLists();
  }, [loadLists]);

  const handleBackClick = () => {
    navigate('/');
  };

  // Helper to navigate to a specific section
  const navigateToSection = (sectionName) => {
    navigate(`/account/${sectionName}`);
  };

  // The Preferences section doesn't need the heavy getLists payload to render its
  // toggles or the priority-slots config (those load from fast, dedicated endpoints).
  // Skip the full-page "Loading your lists..." gate for it so the priority slots
  // appear instantly instead of waiting behind the ~3s lists fetch; the Add-slot
  // dropdown fills in a moment later when lists arrive in the background.
  if (loading && activeSection !== 'preferences') {
    return (
      <div className="App">
        <MobileHeader />
        <div className="app-layout">
          <div className="account-sidebar">
            <div className="sidebar-brand">
              <h1 className="sidebar-brand-title">Windmill</h1>
              <p className="sidebar-brand-tagline">Get stuff done</p>
            </div>
          </div>
          <main className="main-content">
            <div className="content-body">
              <div className="loading-container">
                <div className="spinner"></div>
                <p className="loading-text">Loading your lists...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const getSectionName = (section) => {
    switch (section) {
      case 'list-management': return 'List Management';
      case 'location-aware': return 'Location Aware Tasks';
      case 'recurring': return 'Recurring Tasks';
      case 'preferences': return 'Preferences';
      case 'analytics': return 'Analytics';
      case 'browser-extension': return 'Browser Extension';
      default: return 'Account';
    }
  };

  return (
    <div className="App account-page">
      <MobileHeader />
      <div className="app-layout">
        <div className="account-sidebar">
          <div className="sidebar-brand">
            <div className="sidebar-brand-content">
              <img src="/logo_big.png" alt="Windmill" className="sidebar-brand-logo" />
              <div className="sidebar-brand-text">
                <h1 className="sidebar-brand-title">Windmill</h1>
                <p className="sidebar-brand-tagline">Get stuff done</p>
              </div>
            </div>
          </div>
          <div className="sidebar-header">
            <h2 className="sidebar-title">Account</h2>
          </div>

          <nav className="sidebar-nav">
            <button
              className="sidebar-item sidebar-item-back"
              onClick={handleBackClick}
              aria-label="Back to lists"
            >
              <div className="sidebar-item-content">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 16l-6-6 6-6M6 10h11" />
                </svg>
                <span className="sidebar-item-name">Back to Lists</span>
              </div>
            </button>
            <button
              className={`sidebar-item ${activeSection === 'list-management' ? 'sidebar-item-active' : ''}`}
              onClick={() => navigateToSection('list-management')}
              aria-label="List Management"
            >
              <div className="sidebar-item-content">
                <span className="sidebar-item-name">List Management</span>
              </div>
            </button>
            <button
              className={`sidebar-item ${activeSection === 'location-aware' ? 'sidebar-item-active' : ''}`}
              onClick={() => navigateToSection('location-aware')}
              aria-label="Location Aware Tasks"
            >
              <div className="sidebar-item-content">
                <span className="sidebar-item-name">Location Aware Tasks</span>
              </div>
            </button>
            <button
              className={`sidebar-item ${activeSection === 'recurring' ? 'sidebar-item-active' : ''}`}
              onClick={() => navigateToSection('recurring')}
              aria-label="Recurring Tasks"
            >
              <div className="sidebar-item-content">
                <span className="sidebar-item-name">Recurring Tasks</span>
              </div>
            </button>
            <button
              className={`sidebar-item ${activeSection === 'preferences' ? 'sidebar-item-active' : ''}`}
              onClick={() => navigateToSection('preferences')}
              aria-label="Preferences"
            >
              <div className="sidebar-item-content">
                <span className="sidebar-item-name">Preferences</span>
              </div>
            </button>
            <button
              className={`sidebar-item ${activeSection === 'analytics' ? 'sidebar-item-active' : ''}`}
              onClick={() => navigateToSection('analytics')}
              aria-label="Analytics"
            >
              <div className="sidebar-item-content">
                <span className="sidebar-item-name">Analytics</span>
              </div>
            </button>
            <button
              className={`sidebar-item ${activeSection === 'browser-extension' ? 'sidebar-item-active' : ''}`}
              onClick={() => navigateToSection('browser-extension')}
              aria-label="Browser Extension"
            >
              <div className="sidebar-item-content">
                <span className="sidebar-item-name">Browser Extension</span>
              </div>
            </button>
          </nav>

          <div className="sidebar-footer">
            <button 
              className="sidebar-footer-link" 
              onClick={handleBackClick}
              aria-label="Back to lists"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 16l-6-6 6-6M6 10h11" />
              </svg>
              <span>Back to Lists</span>
            </button>
          </div>
        </div>

        <main className="main-content">
          <div className="content-body">
            {/* Mobile Section Selector */}
            <div className="account-mobile-section-selector">
              <select
                className="account-mobile-section-select"
                value={activeSection}
                onChange={(e) => navigateToSection(e.target.value)}
                aria-label="Select account section"
              >
                <option value="list-management">List Management</option>
                <option value="location-aware">Location Aware Tasks</option>
                <option value="recurring">Recurring Tasks</option>
                <option value="preferences">Preferences</option>
                <option value="analytics">Analytics</option>
                <option value="browser-extension">Browser Extension</option>
              </select>
              <div className="account-mobile-section-display">
                <span className="account-mobile-section-title">
                  {getSectionName(activeSection)}
                </span>
                <svg className="account-mobile-section-chevron" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </div>

            <div className="todo-card">
              <div className="card-header-custom">
                <h2 className="card-title">
                  {getSectionName(activeSection)}
                </h2>
                {activeSection === 'analytics' && (
                  <select
                    value={analyticsDays}
                    onChange={(e) => setAnalyticsDays(Number(e.target.value))}
                    style={{
                      padding: '0.625rem 1.25rem',
                      borderRadius: 'var(--radius-md)',
                      border: '1px solid var(--border-color)',
                      backgroundColor: 'var(--bg-card)',
                      color: 'var(--text-primary)',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      fontWeight: 500,
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = 'var(--primary-color)';
                      e.target.style.boxShadow = '0 2px 4px 0 rgba(0, 0, 0, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = 'var(--border-color)';
                      e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                    }}
                  >
                    <option value={7}>Last 7 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={90}>Last 90 days</option>
                    <option value={180}>Last 6 months</option>
                    <option value={365}>Last year</option>
                  </select>
                )}
              </div>

              <div className="card-body-custom">
                {activeSection === 'list-management' && (
                  <ListManagement user={currentUser} lists={lists} onListsChange={setLists} />
                )}

                {activeSection === 'location-aware' && (
                  <LocationAwareTasks lists={lists} />
                )}

                {activeSection === 'recurring' && (
                  <RecurringTasks lists={lists} />
                )}

                {activeSection === 'preferences' && (
                  <Preferences user={currentUser} onUserUpdate={handleUserUpdate} lists={lists} />
                )}

                {activeSection === 'analytics' && (
                  <Analytics days={analyticsDays} />
                )}

                {activeSection === 'browser-extension' && (
                  <BrowserExtension />
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

